import { useState, useRef, useEffect } from 'react';
import type { PaletteColors } from './avatarTypes';

export type PixelTool = 'paint' | 'erase';

interface PixelEditorProps {
  palette: PaletteColors;
  customColors: string[];
  pixelOverrides: Record<string, string>;
  tool: PixelTool;
  selectedColor: string;
  onToolChange: (tool: PixelTool) => void;
  onColorChange: (color: string) => void;
  onClearAll: () => void;
  onAddCustomColor: (color: string) => void;
  onRemoveCustomColor: (index: number) => void;
  onClose: () => void;
}

const PALETTE_LABELS: { key: keyof PaletteColors; label: string }[] = [
  { key: 'bodyPrimary', label: 'Body 1' },
  { key: 'bodySecondary', label: 'Body 2' },
  { key: 'eyeColor', label: 'Eyes' },
  { key: 'mouthColor', label: 'Mouth' },
  { key: 'accessoryColor', label: 'Acc.' },
  { key: 'backgroundColor', label: 'Bg' },
];

export default function PixelEditor({
  palette,
  customColors,
  pixelOverrides,
  tool,
  selectedColor,
  onToolChange,
  onColorChange,
  onClearAll,
  onAddCustomColor,
  onRemoveCustomColor,
  onClose,
}: PixelEditorProps) {
  const [addingColor, setAddingColor] = useState(false);
  const addColorRef = useRef<HTMLDivElement>(null);

  const SWATCH_COLORS = [
    '#f38ba8', '#eba0ac', '#fab387', '#f9e2af', '#a6e3a1',
    '#94e2d5', '#89b4fa', '#cba6f7', '#b4befe', '#f5c2e7',
    '#ff00ff', '#00ffff', '#00ff41', '#ffff00', '#ff4500',
    '#CCCCCC', '#666666', '#1e1e2e', '#313244', '#1A1A1A',
  ];

  useEffect(() => {
    if (!addingColor) return;
    const handler = (e: MouseEvent) => {
      if (addColorRef.current && !addColorRef.current.contains(e.target as Node)) {
        setAddingColor(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addingColor]);

  const overrideCount = Object.keys(pixelOverrides).length;

  return (
    <div className="malpedia-avatar-pixel-editor">
      <div className="malpedia-avatar-pixel-editor-header">
        <span className="malpedia-avatar-pixel-editor-title">Pixel Editor</span>
        <button className="malpedia-avatar-btn malpedia-avatar-btn--secondary" onClick={onClose}>
          Done
        </button>
      </div>

      {/* Tools */}
      <div className="malpedia-avatar-pixel-tools">
        <button
          className={`malpedia-avatar-pixel-tool${tool === 'paint' ? ' malpedia-avatar-pixel-tool--active' : ''}`}
          onClick={() => onToolChange('paint')}
          title="Paint"
        >
          &#x1F58C;&#xFE0F; Paint
        </button>
        <button
          className={`malpedia-avatar-pixel-tool${tool === 'erase' ? ' malpedia-avatar-pixel-tool--active' : ''}`}
          onClick={() => onToolChange('erase')}
          title="Erase (revert to palette)"
        >
          &#x1F9F9; Erase
        </button>
      </div>

      {/* Palette colors */}
      <div className="malpedia-avatar-pixel-section">
        <span className="malpedia-avatar-pixel-section-label">Palette</span>
        <div className="malpedia-avatar-pixel-colors">
          {PALETTE_LABELS.map(({ key, label }) => (
            <button
              key={key}
              className={`malpedia-avatar-pixel-color${selectedColor === palette[key] && tool === 'paint' ? ' malpedia-avatar-pixel-color--active' : ''}`}
              style={{ backgroundColor: palette[key] }}
              onClick={() => { onColorChange(palette[key]); onToolChange('paint'); }}
              title={`${label}: ${palette[key]}`}
            />
          ))}
        </div>
      </div>

      {/* Custom colors */}
      <div className="malpedia-avatar-pixel-section">
        <span className="malpedia-avatar-pixel-section-label">Custom</span>
        <div className="malpedia-avatar-pixel-colors">
          {customColors.map((c, i) => (
            <button
              key={`${c}-${i}`}
              className={`malpedia-avatar-pixel-color${selectedColor === c && tool === 'paint' ? ' malpedia-avatar-pixel-color--active' : ''}`}
              style={{ backgroundColor: c }}
              onClick={() => { onColorChange(c); onToolChange('paint'); }}
              onContextMenu={(e) => { e.preventDefault(); onRemoveCustomColor(i); }}
              title={`${c} (right-click to remove)`}
            />
          ))}
          {addingColor ? (
            <div className="malpedia-avatar-pixel-add-color" ref={addColorRef}>
              <div className="malpedia-avatar-swatch-grid">
                {SWATCH_COLORS.map((c) => (
                  <button
                    key={c}
                    className="malpedia-avatar-swatch"
                    style={{ backgroundColor: c }}
                    onClick={() => {
                      onAddCustomColor(c);
                      onColorChange(c);
                      setAddingColor(false);
                    }}
                    title={c}
                  />
                ))}
              </div>
            </div>
          ) : (
            <button
              className="malpedia-avatar-pixel-color malpedia-avatar-pixel-color--add"
              onClick={() => setAddingColor(true)}
              title="Add custom color"
            >
              +
            </button>
          )}
        </div>
      </div>

      {/* Status & clear */}
      <div className="malpedia-avatar-pixel-footer">
        <span className="malpedia-avatar-pixel-count">
          {overrideCount} pixel{overrideCount !== 1 ? 's' : ''} painted
        </span>
        {overrideCount > 0 && (
          <button className="malpedia-avatar-btn malpedia-avatar-btn--secondary" onClick={onClearAll}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
}
